import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Handler from "../../Helpers/Handler";
import { formatDateTime } from "../../Helpers/Utils";
import ListLayout from "../../Components/List";
import useListParams from "../../Helpers/useListParams";

const INITIAL_FILTERS = {
    search: "", searchKey: "name", count: 10,
    roleId: "", hubId: "", departmentId: "", employmentType: "", employmentStatus: "",
};

const SEARCH_KEYS = [{ key: "name", value: "Name / employee ID / phone / email" }];

const EMPLOYMENT_STATUS_BADGE = {
    active: "text-bg-success",
    suspended: "text-bg-warning",
    terminated: "text-bg-danger",
};

const StaffList = () => {
    const navigate = useNavigate();
    // Page number in the URL path, filters in the query string - see Helpers/useListParams.
    const { page, setPage, filters, setFilters } = useListParams(INITIAL_FILTERS);
    const [data, setData] = useState({ list: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [hubs, setHubs] = useState([]);
    const [roles, setRoles] = useState([]);

    // Reference lists for both the filter dropdowns below and turning the
    // raw departmentId/hubId this list's API returns into a readable name -
    // fetched once, not per row.
    useEffect(() => {
        (async () => {
            const [departmentsResponse, hubsResponse, rolesResponse] = await Promise.all([
                Handler({ method: "GET", url: "/staff/staff/department/active" }),
                Handler({ method: "GET", url: "/staff/hub/active" }),
                Handler({ method: "post", url: "/staff/staff/role/list/0", params: { count: 100 }, data: { status: true } }),
            ]);
            setDepartments(departmentsResponse?.success ? departmentsResponse?.data?.data || [] : []);
            setHubs(hubsResponse?.success ? hubsResponse?.data?.data || [] : []);
            setRoles(rolesResponse?.success ? rolesResponse?.data?.data?.list || [] : []);
        })();
    }, []);

    const departmentNameOf = useMemo(() => {
        const map = {};
        departments.forEach((d) => { map[d.departmentId] = d.departmentName; });
        return (id) => map[id] || "-";
    }, [departments]);

    const hubNameOf = useMemo(() => {
        const map = {};
        hubs.forEach((h) => { map[h.hubId] = [h.buildingNo, h.area, h.city].filter(Boolean).join(", ") || h.hubId; });
        return (id) => map[id] || id || "-";
    }, [hubs]);

    const filterItems = useMemo(() => ({
        dropdown: [
            {
                key: "employmentStatus",
                name: "Status",
                options: [
                    { key: "", name: "All" },
                    { key: "active", name: "Active" },
                    { key: "suspended", name: "Suspended" },
                    { key: "terminated", name: "Terminated" },
                ],
            },
            {
                key: "employmentType",
                name: "Employment type",
                options: [
                    { key: "", name: "All" },
                    { key: "full-time", name: "Full-time" },
                    { key: "part-time", name: "Part-time" },
                    { key: "contract", name: "Contract" },
                    { key: "intern", name: "Intern" },
                ],
            },
            {
                key: "departmentId",
                name: "Department",
                options: [{ key: "", name: "All" }, ...departments.map((d) => ({ key: d.departmentId, name: d.departmentName }))],
            },
            {
                key: "roleId",
                name: "Role",
                options: [{ key: "", name: "All" }, ...roles.map((r) => ({ key: r.roleId, name: r.roleName }))],
            },
            {
                key: "hubId",
                name: "Hub",
                options: [{ key: "", name: "All" }, ...hubs.map((h) => ({ key: h.hubId, name: [h.buildingNo, h.area, h.city].filter(Boolean).join(", ") || h.hubId }))],
            },
        ],
    }), [departments, roles, hubs]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const response = await Handler({
            method: "post",
            url: `/staff/staff/list/${page - 1}`,
            params: { count: filters.count },
            data: {
                ...(filters.search ? { search: filters.search } : {}),
                ...(filters.roleId ? { roleId: filters.roleId } : {}),
                ...(filters.hubId ? { hubId: filters.hubId } : {}),
                ...(filters.departmentId ? { departmentId: filters.departmentId } : {}),
                ...(filters.employmentType ? { employmentType: filters.employmentType } : {}),
                ...(filters.employmentStatus ? { employmentStatus: filters.employmentStatus } : {}),
            },
        });
        if (response?.success) {
            setData(response?.data?.data || { list: [], total: 0 });
        } else {
            setData({ list: [], total: 0 });
            setError(response?.data?.message || "No data found");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filters.count, filters.search, filters.roleId, filters.hubId, filters.departmentId, filters.employmentType, filters.employmentStatus]);

    useEffect(() => { load(); }, [load]);

    const onFilterChange = (key, value) => {
        if (key !== "count") setPage(1);
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const resetFilter = () => {
        setFilters(INITIAL_FILTERS);
        setPage(1);
    };

    const columns = [
        { key: "employeeId", name: "Employee ID", mandatory: true },
        { key: "name", name: "Name" },
        { key: "designation", name: "Designation" },
        { key: "roleName", name: "Role", render: (row) => row.roleName || <span className="text-muted">-</span> },
        { key: "departmentId", name: "Department", render: (row) => departmentNameOf(row.departmentId) },
        { key: "hubId", name: "Hub", render: (row) => hubNameOf(row.hubId) },
        { key: "employmentType", name: "Type", render: (row) => <span className="text-capitalize">{row.employmentType}</span> },
        {
            key: "employmentStatus",
            name: "Status",
            render: (row) => <span className={`badge rounded-pill text-capitalize ${EMPLOYMENT_STATUS_BADGE[row.employmentStatus] || "text-bg-secondary"}`}>{row.employmentStatus}</span>,
        },
        { key: "createdAt", name: "Joined", render: (row) => formatDateTime(row.createdAt) },
    ];

    return (
        <ListLayout
            title="Staff"
            description="Employee directory - profiles, roles, and employment status."
            data={data}
            loading={loading}
            error={error}
            onRetry={load}
            onRefresh={load}
            table={columns}
            page={page}
            setPageNo={setPage}
            pageSize={filters.count}
            onPageSizeChange={(size) => onFilterChange("count", size)}
            filters={filters}
            initialFilters={INITIAL_FILTERS}
            filterItems={filterItems}
            onFilterChange={onFilterChange}
            resetFilter={resetFilter}
            searchKeys={SEARCH_KEYS}
            searchPlaceholder="Search by name, employee ID, phone, or email..."
            onRowClick={(row) => navigate(`/staff/${row.staffId}`)}
            addList={() => navigate("/staff/new")}
        />
    );
};

export default StaffList;
